import React, { FC, useEffect, useState } from 'react';
import { calculatePriceFromOrderDelayType, updateCart } from './CartAction';
import { getShopRefInStorage } from '../mon-compte/MonCompteAction';
import { Cart, OrderDelayTypePrice } from './iCart';
import { HexaColor, OrderDelayTypeRadio } from '@dp/website-components/dist/types';
import { Dropdown } from '@dp/product-customizer-components';
import Icons from '../mon-compte/Icons';
import { usePriceIsHT } from '../../hooks/use-price-ht';

interface Props {
  cart: Cart;
  excludedPort?: boolean;
  onChange?: () => void;
}

const COLOR_BY_SHOP_REF: Record<string, HexaColor> = {
  TO: '#C09865',
  TE: '#77AF20'
};

const ICON_ORDER_DELAY_TYPE: Record<number, string> = {
  1: '/order/images/ic-bolt.svg'
};

const RenderSelectOrderDelayType: FC<Props> = ({ cart, excludedPort, onChange }): JSX.Element => {
  const [items, setItems] = useState<OrderDelayTypePrice[]>([]);
  const [value, setValue] = useState<number>(0);
  const isHT = usePriceIsHT();

  useEffect(() => {
    const controller = new AbortController();
    calculatePriceFromOrderDelayType(
      cart.shipping_address?.pays?.codePays ?? 'FR',
      excludedPort !== undefined ? !excludedPort : true,
      controller.signal
    )
      .then(res => {
        setItems(res);
        const lessPrice = res.slice().sort((a, b) => a.delay - b.delay)[res.length - 1];
        if (lessPrice && cart.order_delay_type_id === undefined || cart.order_delay_type_id === null) {
          setValue(lessPrice.id);
          updateCart({order_delay_type_id: lessPrice.id, shipping_method_transporteur_id: null }, controller.signal)
            .finally(() => onChange?.());
        }
      });

    if (cart.order_delay_type_id && value !== cart.order_delay_type_id) {
      setValue(cart.order_delay_type_id);
    }
    return () => controller.abort();
  }, [cart]);

  useEffect(() => {
    const controller = new AbortController();
    if (items.map(el => el.id).includes(value)) {
      updateCart({ order_delay_type_id: value, shipping_method_transporteur_id: null }, controller.signal)
        .finally(() => onChange?.());
    }
    return () => controller.abort();
  }, [value]);

  const handleChange = (id: number) => {
    setValue(id);
  };

  const mapItems = (): OrderDelayTypeRadio[] => {
    return items
      .sort((a, b) => a.delay - b.delay)
      .map(item => {
        const dateFrom = new Date(item.cart_delay_max);
        return {
          id: item.id,
          icon: ICON_ORDER_DELAY_TYPE[item.id],
          label: item.label,
          delay: item.delay,
          dateFrom,
          price: isHT ? item.cart_price_ht : item.cart_price_ttc
        };
      }
    );
  };

  const itemSelected = (): undefined|JSX.Element => {
    const selected = mapItems().find(el => el.id === value);
    let result: undefined|JSX.Element = undefined;
    if (selected) {
      result = (
        <div className="d-flex display-flex justify-content-between align-items-center pl-2 w-100" style={{ fontWeight: 'normal' }}>
          <div className="d-flex display-flex align-items-center">
            <Icons icon="local_shipping" size={18} color="#333" />
            <span className="d-block display-block ml-2">{selected.dateFrom.toLocaleDateString(undefined, {weekday: 'long', day: 'numeric', month: 'long'}).toUpperCase()}</span>
            <div className="ml-2 d-flex display-flex align-items-center">
              <span>(</span>
              {selected.icon && (<img src={selected.icon} width="18" height="18" />)}
              <span>{selected.label.toUpperCase()}</span>
              <span>)</span>
            </div>
          </div>
          <div className="d-flex display-flex align-items-center ml-2">
            <Icons icon="arrow_drop_down" size={18} color="#333" />
          </div>
        </div>
      )
    }
    return result;
  };

  return (
    <>
      {items.length > 1 && (
        <Dropdown.Menu className="w-100 col-xl-8 col-12 px-0" btnClassName="w-100 p-2" slotBtn={itemSelected()}>
          {mapItems().map(item => (
            <Dropdown.Item
              key={`odt-key-${item.id}`}
              style={item.id === value ? {backgroundColor: COLOR_BY_SHOP_REF[getShopRefInStorage()]} : undefined}
              onClick={() => handleChange(item.id)}
            >
              <div className="d-flex display-flex align-items-center justify-content-between">
                <div className="d-flex display-flex flex-column">
                  <div className="mr-5 mr3">
                    {item.icon && (<img src={item.icon} width="16" height="16" />)}
                    <span>{item.label.toUpperCase()}{item.delay > 0 && ` J+${item.delay}`}</span>
                  </div>
                  <span className="d-block display-block bold">{item.dateFrom.toLocaleDateString(undefined, {weekday: 'long', day: 'numeric', month: 'long'})}</span>
                </div>
                <div className="d-flex display-flex flex-column text-right">
                  <span className="d-block display-block font12">Total {isHT ? 'HT' : 'TTC'}</span>
                  <span className="d-block display-block bold">{item.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€</span>
                </div>
              </div>
            </Dropdown.Item>
          ))}
          {excludedPort && (
            <Dropdown.Item>
              <span
                style={{textAlign: 'center', fontSize: 12, color: 'grey', display: 'block'}}
              >
                Total {isHT ? 'HT' : 'TTC'} hors frais de livraison
              </span>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      )}
    </>
  );
};

export default RenderSelectOrderDelayType;
